import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PackageSearch, Truck, CheckCircle2, Circle, RotateCcw } from "lucide-react";
import { useCatalog } from "@/context/CatalogContext";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLOW = ["pending", "paid", "shipped", "delivered"];
const LABELS = { pending: "Received", paid: "Confirmed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };

export default function Track() {
  const [params] = useSearchParams();
  const { addItem, setOpen } = useCart();
  const { getProduct } = useCatalog();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (r, e) => {
    if (!r || !e) { toast.error("Enter your order reference and email"); return; }
    setLoading(true); setOrder(null);
    try {
      const { data } = await axios.post(`${API}/orders/lookup`, { ref: r, email: e });
      setOrder(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Order not found"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const r = params.get("ref"), e = params.get("email");
    if (r && e) lookup(r, e);
  }, [params, lookup]);

  const reorder = () => {
    let added = 0;
    order.items.forEach((it) => {
      const p = getProduct(it.id);
      if (p) { addItem(p, it.qty); added++; }
    });
    if (added) { setOpen(true); toast.success("Items added to your cart"); }
    else toast.error("These items are no longer available");
  };

  const cancelled = order?.status === "cancelled";
  const reached = (s) => order && (order.status === s || FLOW.indexOf(order.status) > FLOW.indexOf(s));

  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="track-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">Order Status</p>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-none mb-12">Track Your Order</h1>

        <div className="flex flex-col sm:flex-row gap-3 mb-12">
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Order ref (MLX-…)" data-testid="track-ref-input"
            className="flex-1 bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used at checkout" data-testid="track-email-input"
            onKeyDown={(e) => e.key === "Enter" && lookup(ref, email)}
            className="flex-1 bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
          <button onClick={() => lookup(ref, email)} disabled={loading} data-testid="track-lookup-btn"
            className="bg-[#D4AF37] text-black px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50">
            {loading ? "Searching…" : "Track"}
          </button>
        </div>

        {order && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-zinc-800 bg-[#0C0C0C] p-8" data-testid="track-result">
            <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
              <div>
                <p className="font-serif text-3xl">{order.order_ref}</p>
                <p className="font-mono text-[11px] text-[#71717A] mt-1">Placed {new Date(order.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 border ${cancelled ? "text-red-400 border-red-400/40" : "text-[#D4AF37] border-[#D4AF37]/40"}`} data-testid="track-status">
                {LABELS[order.status]}
              </span>
            </div>

            {!cancelled && (
              <div className="flex items-center justify-between mb-10">
                {FLOW.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className="flex flex-col items-center gap-2">
                      {reached(s) ? <CheckCircle2 size={26} color="#D4AF37" /> : <Circle size={26} className="text-zinc-700" />}
                      <span className={`font-mono text-[10px] uppercase tracking-wide ${reached(s) ? "text-[#F5F5F0]" : "text-[#52525B]"}`}>{LABELS[s]}</span>
                    </div>
                    {i < FLOW.length - 1 && <div className={`flex-1 h-px mx-2 ${reached(FLOW[i + 1]) ? "bg-[#D4AF37]" : "bg-zinc-800"}`} />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {order.tracking && (order.tracking.number || order.tracking.url) && (
              <div className="flex items-center gap-3 border border-zinc-800 p-4 mb-8" data-testid="track-shipping">
                <Truck size={18} color="#D4AF37" />
                <span className="font-mono text-xs text-[#A1A1AA]">
                  {order.tracking.carrier} {order.tracking.number}
                </span>
                {order.tracking.url && <a href={order.tracking.url} target="_blank" rel="noreferrer" className="ml-auto font-mono text-[10px] uppercase tracking-wide text-[#D4AF37] hover:underline">Track shipment →</a>}
              </div>
            )}

            <div className="space-y-2 mb-8">
              {order.items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm border-b border-zinc-900 py-2">
                  <span className="text-[#A1A1AA]">{i.name} ({i.size}) ×{i.qty}</span>
                  <span className="font-mono text-[#D4AF37]">${i.lineTotal}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Total</span>
                <span className="font-serif text-2xl">${order.total}</span>
              </div>
            </div>

            <button onClick={reorder} data-testid="track-reorder-btn"
              className="w-full flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors">
              <RotateCcw size={15} /> Reorder these items
            </button>
          </motion.div>
        )}

        {!order && !loading && (
          <div className="flex flex-col items-center text-center py-16 text-[#52525B]">
            <PackageSearch size={40} strokeWidth={1} className="mb-4" />
            <p className="font-mono text-sm">Enter your order reference and email to view status.</p>
          </div>
        )}
      </div>
    </main>
  );
}
